import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { CardImage } from "@/components/CardImage";
import { FaqSection, faqSchema } from "@/components/PageFaq";
import { breadcrumbSchema } from "@/lib/schema";
import { NotFoundView, DefaultRouteError } from "@/components/RouteFallbacks";
import { palLink } from "@/components/PlayerProfileCard";
import { cardLinkProps } from "@/lib/card-link";
import { OFFICIAL_CARD_BY_CODE, officialCardImage } from "@/lib/tcg-cards";
import { getPlayerProfile } from "@/lib/player-profiles.functions";
import { playstyleLabel, type PlayerProfile } from "@/lib/player-profiles-shared";

const BASE = "https://palstars.com/players";

function faqFor(p: PlayerProfile) {
  return [
    {
      q: `Who is ${p.display_name} on Pal Stars?`,
      a: `${p.display_name} is a Palworld TCG ${playstyleLabel(p.playstyle).toLowerCase()} with a public Pal Stars player profile at /players/${p.handle}${p.region ? `, collecting from ${p.region}` : ""}. The profile lists ${p.wants} card${p.wants === 1 ? "" : "s"} they are hunting and ${p.haves} spare card${p.haves === 1 ? "" : "s"} available to trade, by exact printed card code.`,
    },
    {
      q: `Is ${p.display_name} open to Palworld card trades?`,
      a: p.trade_ready
        ? `Yes — ${p.display_name} is marked open to trades. Compare their spares with the cards you need, then send a card-for-card offer from the Pal Stars trading page.`
        : `${p.display_name} is not currently marked as open to trades, so treat the wants and spares here as a collection snapshot rather than an open offer.`,
    },
    {
      q: "How do I trade with a trainer listed on Pal Stars?",
      a: "Open the want-list board, find a card that is matched (one member wants it, another has a spare), then send a card-for-card offer from the trading page. Every listing uses the exact printed English card code, so both sides know the printing before the swap.",
    },
  ];
}

export const Route = createFileRoute("/players/$handle")({
  loader: async ({ params }) => {
    const profile = await getPlayerProfile({ data: { handle: params.handle } });
    if (!profile) throw notFound();
    return { profile };
  },
  errorComponent: DefaultRouteError,
  notFoundComponent: () => (
    <NotFoundView
      title="Trainer profile not found"
      message="That Pal Stars player profile does not exist or is not published."
    />
  ),
  head: ({ params, loaderData }) => {
    const p = loaderData?.profile;
    const url = `${BASE}/${params.handle}`;
    if (!p) {
      return {
        meta: [
          { title: "Palworld TCG Trainer Not Found | Pal Stars" },
          { name: "robots", content: "noindex, follow" },
        ],
      };
    }
    const title = `${p.display_name} — Palworld TCG Player Profile`;
    const description = `${p.display_name} is a Palworld TCG ${playstyleLabel(p.playstyle).toLowerCase()}${
      p.region ? ` from ${p.region}` : ""
    } with ${p.wants} wanted cards and ${p.haves} spares listed for trade on Pal Stars.`;
    return {
      meta: [
        { title: title.length > 60 ? `${p.display_name} — Palworld TCG Trainer` : title },
        { name: "description", content: description.slice(0, 158) },
        { property: "og:title", content: `${p.display_name} — Palworld TCG Player Profile` },
        { property: "og:description", content: description.slice(0, 158) },
        { property: "og:type", content: "profile" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "index, follow, max-snippet:-1, max-image-preview:large" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        breadcrumbSchema([
          ["Home", "/"],
          ["Player Profiles", "/players"],
          [p.display_name, `/players/${p.handle}`],
        ]),
        faqSchema(faqFor(p), url),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfilePage",
            "@id": `${url}#profile`,
            url,
            name: `${p.display_name} — Palworld TCG player profile`,
            description,
            mainEntity: {
              "@type": "Person",
              name: p.display_name,
              alternateName: p.handle,
              description: p.bio || description,
              url,
            },
            isPartOf: { "@type": "WebSite", name: "Pal Stars", url: "https://palstars.com" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SpeakableSpecification",
            cssSelector: [".aeo-answer"],
          }),
        },
      ],
    };
  },
  component: PlayerPage,
});

function CardStrip({ codes, heading }: { codes: string[]; heading: string }) {
  if (codes.length === 0) {
    return (
      <div>
        <h2 className="text-sm uppercase tracking-wide text-muted-foreground">{heading}</h2>
        <p className="mt-2 text-sm text-muted-foreground">Nothing listed yet.</p>
      </div>
    );
  }
  return (
    <div>
      <h2 className="text-sm uppercase tracking-wide text-muted-foreground">
        {heading} ({codes.length})
      </h2>
      <ul className="mt-2 flex flex-wrap gap-3">
        {codes.map((code) => {
          const card = OFFICIAL_CARD_BY_CODE.get(code);
          const image = card?.image ?? officialCardImage(code);
          return (
            <li key={code}>
              <Link {...cardLinkProps(code)} className="block text-center">
                {image ? (
                  <CardImage
                    src={image}
                    alt={`${card?.name ?? code} (${code}) Palworld TCG card`}
                    width={78}
                    height={109}
                  />
                ) : (
                  <span className="flex h-[109px] w-[78px] items-center justify-center rounded-md border border-border/50 text-[10px]">
                    {code}
                  </span>
                )}
                <span className="mt-1 block font-mono text-[10px] text-muted-foreground">{code}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function PlayerPage() {
  const { profile } = Route.useLoaderData() as { profile: PlayerProfile };
  const pal = palLink(profile.favorite_pal);
  const card = profile.favorite_card ? OFFICIAL_CARD_BY_CODE.get(profile.favorite_card) : undefined;
  const faq = faqFor(profile);

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Palworld TCG player profile</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">{profile.display_name}</h1>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          <span className="rounded-full border border-border/60 px-2 py-0.5">
            {playstyleLabel(profile.playstyle)}
          </span>
          {profile.region && <span className="rounded-full border border-border/60 px-2 py-0.5">{profile.region}</span>}
          <span
            className={`rounded-full px-2 py-0.5 font-semibold ${
              profile.trade_ready ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
            }`}
          >
            {profile.trade_ready ? "Open to trades" : "Not trading right now"}
          </span>
          <span className="font-mono text-muted-foreground">/players/{profile.handle}</span>
        </div>
        <p className="aeo-answer mt-4 max-w-3xl text-muted-foreground">
          {profile.bio ||
            `${profile.display_name} is a Palworld TCG ${playstyleLabel(profile.playstyle).toLowerCase()} on Pal Stars with ${profile.wants} wanted cards and ${profile.haves} spares listed by exact printed card code.`}
        </p>
      </header>

      <section className="mt-8 glass rounded-2xl p-5">
        <dl className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
          <div>
            <dt className="text-xs text-muted-foreground">Cards wanted</dt>
            <dd className="text-lg font-bold">{profile.wants}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Spares to trade</dt>
            <dd className="text-lg font-bold">{profile.haves}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Favourite Pal</dt>
            <dd className="font-semibold">
              {pal ? (
                <Link to="/pals/$slug" params={{ slug: pal.slug }} className="text-primary hover:underline">
                  {pal.name}
                </Link>
              ) : (
                profile.favorite_pal || "—"
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Favourite card</dt>
            <dd className="font-semibold">
              {profile.favorite_card ? (
                <Link {...cardLinkProps(profile.favorite_card)} className="text-primary hover:underline">
                  {card?.name ?? profile.favorite_card}
                </Link>
              ) : (
                "—"
              )}
            </dd>
          </div>
        </dl>
      </section>

      <section className="mt-8 glass rounded-2xl p-5 space-y-6">
        <CardStrip codes={profile.want_codes} heading="Cards this trainer wants" />
        <CardStrip codes={profile.have_codes} heading="Spare cards for trade" />
        <p className="text-sm text-muted-foreground">
          See the whole board on the{" "}
          <Link to="/want-list" className="text-primary hover:underline">
            Palworld TCG want list
          </Link>
          , send a swap from{" "}
          <Link to="/trades" className="text-primary hover:underline">
            card trading
          </Link>
          , or price a card on the{" "}
          <Link to="/prices" className="text-primary hover:underline">
            price chart
          </Link>
          .
        </p>
      </section>

      <FaqSection items={faq} heading={`${profile.display_name} — trainer FAQ`} />

      <p className="mt-8 text-sm">
        <Link to="/players" className="text-primary hover:underline">
          ← All Palworld TCG player profiles
        </Link>
      </p>
    </div>
  );
}
